import React, { useEffect, useState } from 'react';
import { Form, Input, Radio, Select, Button, Modal, Space, Row, Col, Card, Upload, message } from 'antd';
import '@app/styles/form-modal.css';
import { PlusOutlined, CameraOutlined } from '@ant-design/icons';
import { CustomerModel } from '@app/interfaces/customer';
import InputMask from 'react-input-mask';
import { useAppSelector, useAppDispatch } from '@app/hooks/reduxHooks';
import { RootState } from '@app/store/store';
import { createCustomerGroup, getCustomerGroups } from '@app/store/slices/customerGroupSlice';
import { getDistrictsApi, getProvincesApi, getWardsApi } from '@app/api/province.api';
import CustomerGroupForm from './CustomerGroupForm';
import ImagePreviewAvatar from '@app/components/common/ImagePreviewAvatar';
import { getFullBlobUrl } from '@app/utils/blobUrl';

interface ModalProps {
  open: boolean;
  onCreate: (values: any) => void;
  onCancel: () => void;
  title: string;
  record: CustomerModel | null;
}

const CustomerForm: React.FC<ModalProps> = ({ open, onCreate, onCancel, title, record }) => {
  const [form] = Form.useForm();
  const [provinceOptions, setProvinceOptions] = useState<any>([]);
  const [districtOptions, setDistrictOptions] = useState<any>([]);
  const [wardOptions, setWardOptions] = useState<any>([]);
  const [provinceCode, setProvinceCode] = useState<string>('');
  const [showCustomerGroupForm, setShowCustomerGroupForm] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string>("");
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  const { data: customerGroups = [] } = useAppSelector((state: RootState) => state.customer_group || {});
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (record) {
      form.setFieldsValue(record);
      if (record.avatar_url) {
        setAvatarPreview(getFullBlobUrl(record.avatar_url) || "");
      }

      const customer_group_id = record.customer_group?.id;
      if (customer_group_id) {
        form.setFieldsValue({ customer_group_id: customer_group_id });
      }

      if (record.province_code) {
        fetchDistricts(record.province_code);
        setProvinceCode(record.province_code);
      }

      // If we have both province_code and district_code, load wards
      if (record.province_code && record.district_code) {
        fetchWards(record.province_code, record.district_code);
      }
    }
  }, [record]);

  useEffect(() => {
    fetchCustomerGroup();
    fetchProvinces();
  }, []);

  const fetchCustomerGroup = async () => {
    await dispatch(getCustomerGroups());
  };

  const fetchProvinces = async () => {
    const repsonse = await getProvincesApi();
    if (repsonse) {
      const options = repsonse && repsonse.map((item: any) => ({ value: item.province_code, label: item.name }));
      setProvinceOptions(options);
    }
  };

  const fetchDistricts = async (provinceCode: string) => {
    if (!provinceCode) return;
    setProvinceCode(provinceCode);

    const repsonse = await getDistrictsApi(provinceCode);
    if (repsonse) {
      const options = repsonse && repsonse.map((item: any) => ({ value: item.district_code, label: item.name }));
      setDistrictOptions(options);
    }
  };

  const fetchWards = async (provinceCode: string, districtCode: string) => {
    if (!provinceCode && !districtCode) return;

    const repsonse = await getWardsApi(provinceCode, districtCode);
    if (repsonse) {
      const options = repsonse && repsonse.map((item: any) => ({ value: item.ward_code, label: item.name }));
      setWardOptions(options);
    }
  };

  const customerGroupOptions = customerGroups && customerGroups.map((item) => ({ value: item.id, label: item.name }));

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        //form.resetFields();
        const currentYear = new Date().getFullYear();
        const birthDay = values.date_of_birth ? `${values.date_of_birth}-${currentYear}` : null;

        const allValues = { ...values, date_of_birth: birthDay, store_id: 1 };

        // If we have an avatar file, create FormData
        if (avatarFile) {
          const formData = new FormData();
          Object.keys(allValues).forEach((key) => {
            if (allValues[key] !== undefined && allValues[key] !== null) {
              formData.append(key, allValues[key]);
            }
          });
          formData.append('avatar', avatarFile);
          setIsUploadingAvatar(true);
          onCreate(formData);
          setIsUploadingAvatar(false);
        } else {
          onCreate(allValues);
        }
      })
      .catch((info) => {
        console.log('Validate failed:', info);
      });
  };

  const handleAvatarUpload = (file: File) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Vui lòng tải lên một tập tin hình ảnh!');
      return false;
    }

    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Hình ảnh phải nhỏ hơn 2MB!');
      return false;
    }

    setAvatarFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handleRemoveAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview("");
  };

  const handleAddCustomerGroup = () => {
    setShowCustomerGroupForm(true);
  };

  const onCreateCustomerGroup = (values: any) => {
    try {
      dispatch(createCustomerGroup(values));
      setShowCustomerGroupForm(false);
    } catch (error: any) {
      console.log(error.message);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onCancel}
        width={1000}
        closable={false}
        className="form-modal"
        footer={
          <Space>
            <Button onClick={handleSubmit} type="primary" loading={isUploadingAvatar}>Lưu</Button>
            <Button onClick={onCancel}>Bỏ qua</Button>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          name="form-customer"
          initialValues={{
            customer_type: 'individual',
            gender: false,
          }}
          size="small"
          autoComplete="off"
        >
          {/* Hidden input to prevent autofill */}
          <input type="text" name="fake-input" autoComplete="off" aria-hidden="true" className="visually-hidden" />

          <h3 className="mb-4 font-bold-weight">{title}</h3>
          <Row gutter={12}>
            <Col span={12}>
              <Card>
                <Row gutter={12}>
                  <Col span={7}></Col>
                  <Col span={17}>
                    <Form.Item name="customer_type">
                      <Radio.Group>
                        <Radio value="individual">Cá nhân</Radio>
                        <Radio value="business">Doanh nghiệp</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={7}>Tên khách hàng</Col>
                  <Col span={17}>
                    <Form.Item name="name" rules={[{ required: true, message: 'Please input customer name!' }]}>
                      <Input placeholder="Tên khách hàng" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={7}>Số điện thoại</Col>
                  <Col span={17}>
                    <Form.Item name="phone">
                      <Input placeholder="Số điện thoại (không bắt buộc)" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={7}>Email</Col>
                  <Col span={17}>
                    <Form.Item
                      name="email"
                      rules={[
                        {
                          type: 'email',
                          message: 'The input is not valid email!',
                        },
                        // {
                        //   required: true,
                        //   message: 'Please input your email!',
                        // },
                      ]}
                    >
                      <Input placeholder="Email" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={7}>Ngày sinh (DD-MM)</Col>
                  <Col span={17}>
                    <Form.Item
                      name="date_of_birth"
                      rules={[
                        //{ required: true, message: 'Please enter your date of birth!' },
                        {
                          pattern: /^\d{2}\-\d{2}/,
                          message: 'Please enter a valid date in the format DD-MM',
                        },
                      ]}
                    >
                      <InputMask mask="99-99" placeholder="DD-MM">
                        {(inputProps: any) => <Input {...inputProps} />}
                      </InputMask>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={7}>Phái</Col>
                  <Col span={17}>
                    <Form.Item name="gender">
                      <Radio.Group>
                        <Radio value={false}>Nam</Radio>
                        <Radio value={true}>Nữ</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col span={12}>
              <Card>
                {/* Avatar Upload Section */}
                <Row gutter={12} className="form-modal__avatar-row">
                  <Col span={24}>
                    <div className="form-modal__avatar-wrap">
                      <div>
                        <ImagePreviewAvatar
                          src={avatarPreview}
                          size={80}
                          onRemove={handleRemoveAvatar}
                          showRemoveButton={!!avatarPreview}
                        />
                      </div>
                      <Upload
                        maxCount={1}
                        beforeUpload={handleAvatarUpload}
                        showUploadList={false}
                        accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                      >
                        <Button
                          type="primary"
                          size="small"
                          loading={isUploadingAvatar}
                          icon={<CameraOutlined />}
                        >
                          Tải lên ảnh đại diện
                        </Button>
                      </Upload>
                    </div>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={7}>Nhóm khách hàng</Col>
                  <Col span={17}>
                    <Form.Item
                      name="customer_group_id"
                      rules={[{ required: true, message: 'Please select a customer group!' }]}
                    >
                      <Select
                        allowClear
                        showSearch
                        placeholder="Chọn nhóm khách hàng"
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={customerGroupOptions}
                        suffixIcon={<PlusOutlined className="text-primary" onClick={handleAddCustomerGroup} />}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={7}>Tỉnh/thành phố</Col>
                  <Col span={17}>
                    <Form.Item
                      name="province_code"
                      rules={[{ required: true, message: 'Please select a province/city!' }]}
                    >
                      <Select
                        allowClear
                        showSearch
                        placeholder="Chọn Tỉnh/thành phố"
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                          const label = option?.label;
                          return typeof label === 'string' && label.toLowerCase().includes(input.toLowerCase());
                        }}
                        options={provinceOptions}
                        onChange={(value: string) => {
                          fetchDistricts(value);
                          form.setFieldsValue({ district_code: null });
                          form.setFieldsValue({ ward_code: null });
                        }}
                        onFocus={(e) => {
                          e.target.setAttribute('autocomplete', 'new-password');
                          e.target.setAttribute('autocorrect', 'off');
                          e.target.setAttribute('spellcheck', 'false');
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={7}>Quận/Huyện</Col>
                  <Col span={17}>
                    <Form.Item
                      name="district_code"
                      //rules={[{ required: true, message: 'Please select a district!' }]}
                    >
                      <Select
                        allowClear
                        showSearch
                        placeholder="Chọn Quận/Huyện"
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                          const label = option?.label;
                          return typeof label === 'string' && label.toLowerCase().includes(input.toLowerCase());
                        }}
                        options={districtOptions}
                        onChange={(value: string) => {
                          fetchWards(provinceCode, value);
                          form.setFieldsValue({ ward_code: null });
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={7}>Phường/Xã</Col>
                  <Col span={17}>
                    <Form.Item
                      name="ward_code"
                      //rules={[{ required: true, message: 'Please select a ward!' }]}
                    >
                      <Select
                        allowClear
                        showSearch
                        placeholder="Chọn Phường/Xã"
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                          const label = option?.label;
                          return typeof label === 'string' && label.toLowerCase().includes(input.toLowerCase());
                        }}
                        options={wardOptions}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={7}>Địa chỉ</Col>
                  <Col span={17}>
                    <Form.Item name="address">
                      <Input.TextArea placeholder="Số nhà, tên đường" autoSize={{ minRows: 3, maxRows: 5 }} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>

          <Card className="mt-3">
            <Row gutter={12}>
              <Col span={5}>Tên công ty/Trường học</Col>
              <Col span={19}>
                <Form.Item
                  name="company_name"
                  //rules={[{ required: true, message: 'Please input company name!' }]}
                >
                  <Input placeholder="Tên công ty/Trường học" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={5}>Chức vụ</Col>
              <Col span={19}>
                <Form.Item name="title">
                  <Input placeholder="Chức vụ" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={5}>Địa chỉ công ty/Trường học</Col>
              <Col span={19}>
                <Form.Item name="company_address">
                  <Input.TextArea placeholder="Địa chỉ công ty/Trường học" autoSize={{ minRows: 3, maxRows: 5 }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={12}>
              <Col span={5}>Ghi chú</Col>
              <Col span={19}>
                <Form.Item name="remark">
                  <Input.TextArea placeholder="Ghi chú" autoSize={{ minRows: 3, maxRows: 5 }} />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Form>
      </Modal>

      {showCustomerGroupForm && (
        <CustomerGroupForm
          open={showCustomerGroupForm}
          onCreate={onCreateCustomerGroup}
          onCancel={() => {
            setShowCustomerGroupForm(false);
          }}
          title="Tạo nhóm khách hàng"
        />
      )}
    </>
  );
};

export default CustomerForm;
